class CartPopup extends HTMLElement {
  constructor() {
    super();
    this._cart = null;
    this._lastProductId = null;
    this._changingQty = false;

    this._panel      = this.querySelector('[data-cart-popup-panel]');
    this._overlay    = this.querySelector('[data-cart-popup-overlay]');
    this._itemsEl    = this.querySelector('[data-cart-popup-items]');
    this._countEl    = this.querySelector('[data-cart-popup-count]');
    this._totalEl    = this.querySelector('[data-cart-popup-total]');
    this._dealsList    = this.querySelector('[data-cart-popup-deals-list]');
    this._shippingText = this.querySelector('[data-cart-popup-shipping-text]');

    // Expose cart popup instance globally (Task 4 depends on this)
    window.cartPopup = this;

    // Close triggers
    this.querySelector('[data-cart-popup-close]')
      ?.addEventListener('click', () => this.close());
    this.addEventListener('click', (e) => {
      if (!this._panel?.contains(e.target)) this.close();
    });
    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && this.hasAttribute('open')) this.close();
    });

    // Click-to-open: intercept cart icon link, open drawer instead of navigating
    const cartIcon = (this.closest('.main-header__cart') || this.parentElement)
      ?.querySelector('.header__cart-icon');
    if (cartIcon) {
      cartIcon.addEventListener('click', (e) => {
        e.preventDefault();
        this._openWithFetch();
      });
    }
  }

  open() {
    this.setAttribute('open', '');
  }

  close() {
    this.removeAttribute('open');
  }

  // Called on hover — fetches fresh cart data then opens
  _openWithFetch() {
    if (this.hasAttribute('open')) return;
    fetch('/cart.js')
      .then((r) => r.json())
      .then((cart) => {
        this._cart = cart;
        const merchandiseTotal = this._itemsTotal(cart.items);
        this._renderCount(cart.item_count);
        this._renderTotal(merchandiseTotal);
        this._renderItems(cart.items);
        this._renderShippingBar(merchandiseTotal);
        this.open();
        this._fetchDeals();
      })
      .catch(() => this.open()); // open anyway on error
  }

  // Called from ajaxCart() after add-to-cart — cart is the full cart JSON
  renderContents(cart, productId) {
    this._cart = cart;
    this._lastProductId = productId || null;
    const merchandiseTotal = this._itemsTotal(cart.items);
    this._renderCount(cart.item_count);
    this._renderTotal(merchandiseTotal);
    this._renderItems(cart.items);
    this._renderShippingBar(merchandiseTotal);
    this.open();
    this._fetchDeals();
  }

  _renderCount(count) {
    this._countEl.textContent = count;
    // Also update the header cart icon badge
    document.querySelectorAll('[data-cart-icon-bubble] .cart-count-bubble span[aria-hidden]')
      .forEach((el) => { el.textContent = count; });
  }

  _renderTotal(totalPrice) {
    // totalPrice is in cents (Shopify integer format)
    this._totalEl.textContent = this._formatMoney(totalPrice);
  }

  _renderItems(items) {
    if (!items || items.length === 0) {
      this._itemsEl.innerHTML = `
        <div class="cart-popup__empty">
          <p>Your cart is empty.</p>
          <a href="/collections/all" class="cart-popup__viewcart-btn">Shop now</a>
        </div>`;
      return;
    }

    this._itemsEl.innerHTML = items.map((item) => `
      <div class="cart-popup__item" data-item-key="${item.key}">
        <a href="${item.url}" class="cart-popup__item-img-wrap">
          <img src="${this._imgUrl(item.image, 116)}"
               alt="${this._escape(item.product_title)}"
               width="58" height="58" loading="lazy">
        </a>
        <div class="cart-popup__item-body">
          <div class="cart-popup__item-name">
            <a href="${item.url}">${this._escape(item.product_title)}</a>
          </div>
          ${item.variant_title && item.variant_title !== 'Default Title'
            ? `<div class="cart-popup__item-variant">${this._escape(item.variant_title)}</div>`
            : ''}
          <div class="cart-popup__qty-row">
            <button class="cart-popup__qty-btn"
                    data-qty-action="minus"
                    data-key="${item.key}"
                    aria-label="Decrease quantity">−</button>
            <span class="cart-popup__qty-num">${item.quantity}</span>
            <button class="cart-popup__qty-btn"
                    data-qty-action="plus"
                    data-key="${item.key}"
                    aria-label="Increase quantity">+</button>
          </div>
        </div>
        <div class="cart-popup__item-right">
          <span class="cart-popup__item-price">${this._formatMoney(item.final_line_price)}</span>
          <button class="cart-popup__remove"
                  data-remove-key="${item.key}"
                  aria-label="Remove ${this._escape(item.title)}">✕</button>
        </div>
      </div>
    `).join('');

    // Wire qty and remove buttons
    this._itemsEl.querySelectorAll('[data-qty-action]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const key = btn.dataset.key;
        const delta = btn.dataset.qtyAction === 'plus' ? 1 : -1;
        this._handleQtyChange(key, delta);
      });
    });

    this._itemsEl.querySelectorAll('[data-remove-key]').forEach((btn) => {
      btn.addEventListener('click', () => this._handleRemove(btn.dataset.removeKey));
    });
  }

  _handleQtyChange(key, delta) {
    const item = this._cart.items.find(i => i.key === key);
    if (!item) return;
    const newQty = item.quantity + delta;
    this._changeQuantity(key, newQty < 0 ? 0 : newQty);
  }

  _handleRemove(key) {
    this._changeQuantity(key, 0);
  }

  _changeQuantity(key, quantity) {
    if (this._changingQty) return;
    this._changingQty = true;

    this._itemsEl.querySelectorAll('[data-qty-action], [data-remove-key]')
      .forEach(b => { b.disabled = true; });

    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    })
      .then((r) => r.json())
      .then((cart) => {
        this._cart = cart;
        const merchandiseTotal = this._itemsTotal(cart.items);
        this._renderCount(cart.item_count);
        this._renderTotal(merchandiseTotal);
        this._renderShippingBar(merchandiseTotal);
        this._renderItems(cart.items);
      })
      .catch((e) => console.error('[CartPopup] qty change failed', e))
      .finally(() => { this._changingQty = false; });
  }

  // ── Helpers ──

  _itemsTotal(items) {
    return (items || []).reduce((sum, item) => sum + item.final_line_price, 0);
  }

  _formatMoney(cents) {
    if (!Number.isFinite(cents)) cents = 0;
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents, window.theme?.moneyFormat || '${{amount}} NZD');
    }
    return '$' + (cents / 100).toFixed(2) + ' NZD';
  }

  _imgUrl(src, size) {
    if (!src) return '';
    // Shopify CDN URLs may have query params — insert size before extension, preserve params
    return src.replace(/(\.(jpg|jpeg|png|gif|webp))(\?.*)?$/i, `_${size}x$1$3`);
  }

  _escape(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // ── Shipping message (text only — no bar graphic) ──

  _renderShippingBar(totalPrice) {
    const threshold = 9900;
    if (!Number.isFinite(totalPrice)) totalPrice = 0;
    if (!this._shippingText) return;
    if (totalPrice >= threshold) {
      this._shippingText.innerHTML = '🎉 You\'ve unlocked <strong>free shipping</strong>!';
      this._shippingText.className = 'cart-popup__shipping-text cart-popup__shipping-text--done';
    } else {
      const remaining = threshold - totalPrice;
      this._shippingText.innerHTML = `You're <strong>${this._formatMoney(remaining)}</strong> away from <strong>free shipping</strong>!`;
      this._shippingText.className = 'cart-popup__shipping-text';
    }
  }

  // ── Last Chance Savings deals strip ──

  async _fetchDeals() {
    if (!this._dealsList) return;
    try {
      const res = await fetch('/collections/2026-clearance/products.json?limit=10');
      if (!res.ok) return;
      const { products } = await res.json();
      this._renderDeals(products || []);
    } catch {}
  }

  _renderDeals(products) {
    const section = this._dealsList?.closest('.cart-popup__deals');
    if (!section) return;
    if (!products.length) {
      section.style.display = 'none';
      return;
    }
    section.style.display = '';

    this._dealsList.innerHTML = products.map((p) => {
      const v = p.variants[0];
      const src = p.images[0]?.src?.replace(/(\.[^.]+)$/, '_120x120$1') ?? '';
      const price = parseFloat(v.price) * 100;
      const url = `/products/${this._escape(p.handle)}`;
      return `<div class="cart-popup__deal-row">
        <a href="${url}" class="cart-popup__deal-img-link" tabindex="-1">
          <img class="cart-popup__deal-img" src="${this._escape(src)}" alt="${this._escape(p.title)}" loading="lazy">
        </a>
        <div class="cart-popup__deal-info">
          <a href="${url}" class="cart-popup__deal-name">${this._escape(p.title)}</a>
          <div class="cart-popup__deal-price">${this._formatMoney(price)}</div>
        </div>
        <button class="cart-popup__deal-add" data-deal-add data-variant-id="${v.id}">Add</button>
      </div>`;
    }).join('');

    this._dealsList.querySelectorAll('[data-deal-add]').forEach((btn) => {
      btn.addEventListener('click', () => this._addDeal(btn));
    });
  }

  async _addDeal(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = '✓';
    try {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });
      const cart = await fetch('/cart.js').then((r) => r.json());
      this._cart = cart;
      const merchandiseTotal = this._itemsTotal(cart.items);
      this._renderCount(cart.item_count);
      this._renderTotal(merchandiseTotal);
      this._renderShippingBar(merchandiseTotal);
      this._renderItems(cart.items);
      this._fetchDeals();
    } catch {
      btn.disabled = false;
      btn.textContent = 'Add';
    }
  }
}

customElements.define('cart-popup', CartPopup);
